import React, { useMemo, useState } from 'react';
import styled from 'styled-components';
import SEOMetadata from '@/components/SEOMetadata';
import { DEFAULT_API_BASE } from '@/lib/tickerReport';
import {
  TrendingUp,
  TrendingDown,
  Building2,
  Briefcase,
  Globe2,
  Users,
  DollarSign,
  BarChart3,
  Percent,
  Activity,
  Gauge,
  Link2,
  Network,
  Shield,
  Newspaper,
  UserCheck,
  Landmark,
  Star,
  Code2,
  Database,
  CheckCircle2,
  Info,
  ExternalLink,
  ArrowRight,
  ArrowUpRight,
  Sparkles,
  AlertCircle,
  Search,
  Copy,
  Check,
  Rocket,
  FlaskConical,
  Award,
} from 'lucide-react';
import {
  AreaChart,
  Area,
  BarChart,
  Bar,
  LineChart,
  Line,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';

const loadFooter = () => import('@/components/landing/Footer');
const Footer = React.lazy(loadFooter);

const loadGetStarted = () => import('@/components/landing/GetStarted');
const GetStarted = React.lazy(loadGetStarted);

/**
 * AxionTickerReport
 * ==================================================================
 * Programmatic-SEO report page for a single STOCK ticker, built on
 * Axion's financial data API. Renders company identity, price
 * history, fundamentals, valuation, ownership, ETF/fund exposure,
 * supply chain, ESG, sentiment, filings, news, code examples and
 * conversion-focused copy/CTAs for the API itself.
 *
 * Visual language: the Axion landing-page system. A clean hairline
 * masthead, serif section headings with numbered figures under each
 * chart ("Fig. 01"), serif display/body type paired with a monospace
 * "data" register for anything quantitative, and soft, rounded cards and
 * pills throughout.
 *
 * Only required prop: `ticker`.
 *
 * ---- SSG usage (data fetched at build time) ----------------------
 *   <AxionTickerReport ticker={ticker} initialData={initialData} />
 *
 * Data is fetched server-side at build time via `fetchTickerReportData()`
 * (see scripts/fetch-ticker-data.mjs) and passed as `initialData`. This
 * component never fetches from the browser, so no API key ships to clients.
 *
 * NOTE ON FIELD NAMES: response shapes below are assembled from
 * Axion's published SDK docs and sample payloads. A few fields
 * (marked inline) are best-effort guesses at naming conventions the
 * API follows elsewhere - every read in this file is optional-chained
 * and every section hides itself when its data is absent, so a wrong
 * guess degrades gracefully instead of breaking the page. Verify
 * against your own account's responses and adjust as needed.
 * ==================================================================
 */

// ============================================================================
// Formatting + data-shaping helpers
// ============================================================================

const RANGE_OPTIONS = ['1Y', '5Y', '10Y', 'MAX'];

// ============================================================================
// Formatting + data-shaping helpers
// ============================================================================

const compactCurrency = (value) => {
  const n = Number(value);
  if (!Number.isFinite(n)) return '-';
  const abs = Math.abs(n);
  const sign = n < 0 ? '-' : '';
  if (abs >= 1e12) return `${sign}$${(abs / 1e12).toFixed(2)}T`;
  if (abs >= 1e9) return `${sign}$${(abs / 1e9).toFixed(2)}B`;
  if (abs >= 1e6) return `${sign}$${(abs / 1e6).toFixed(2)}M`;
  if (abs >= 1e3) return `${sign}$${(abs / 1e3).toFixed(1)}K`;
  return `${sign}$${abs.toFixed(2)}`;
};

const compactNumber = (value) => {
  const n = Number(value);
  if (!Number.isFinite(n)) return '-';
  const abs = Math.abs(n);
  const sign = n < 0 ? '-' : '';
  if (abs >= 1e9) return `${sign}${(abs / 1e9).toFixed(2)}B`;
  if (abs >= 1e6) return `${sign}${(abs / 1e6).toFixed(2)}M`;
  if (abs >= 1e3) return `${sign}${(abs / 1e3).toFixed(1)}K`;
  return `${sign}${abs.toFixed(0)}`;
};

const formatPercent = (value, { decimals = 1, alreadyScaled = false } = {}) => {
  const n = Number(value);
  if (!Number.isFinite(n)) return '-';
  const pct = alreadyScaled ? n : n * 100;
  return `${pct.toFixed(decimals)}%`;
};

const formatPrice = (value, currency = 'USD') => {
  const n = Number(value);
  if (!Number.isFinite(n)) return '-';
  try {
    return n.toLocaleString('en-US', { style: 'currency', currency, maximumFractionDigits: 2 });
  } catch {
    return `$${n.toFixed(2)}`;
  }
};

const formatDateShort = (dateStr) => {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  if (Number.isNaN(d.getTime())) return String(dateStr).slice(0, 10);
  return d.toLocaleDateString('en-US', { month: 'short', year: '2-digit' });
};

const formatDateFull = (dateStr) => {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  if (Number.isNaN(d.getTime())) return String(dateStr).slice(0, 10);
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

const formatMastheadDate = (d = new Date()) =>
  d.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });

const truncate = (str, n) => {
  if (!str) return '';
  return str.length > n ? `${str.slice(0, n).trim()}…` : str;
};

const parseMoneyString = (str) => {
  if (str === null || str === undefined) return NaN;
  return Number(String(str).replace(/,/g, ''));
};

// Shortens a legal company name for SEO titles by dropping trailing legal
// suffixes and share-class markers ("Meta Platforms Inc Class A" →
// "Meta Platforms") so ticker titles stay within ~60 characters.
const shortCompanyName = (name: string) => {
  if (!name) return name;
  return name
    .replace(/,?\s+(Inc\.?|Incorporated|Corp\.?|Corporation|Company|Companies|Co\.?|PLC|Ltd\.?|Limited|S\.?A\.?|N\.?V\.?|AG|AB|ASA|Group|Holdings|International)$/i, '')
    .replace(/,?\s+Class\s+[A-Z]+$/i, '')
    .replace(/,?\s+(Inc\.?|Incorporated|Corp\.?|Corporation|Holdings)$/i, '')
    .trim() || name;
};

// Final ticker title budget: " | AxionQuant" (13) + this title must stay ≤65.
const tickerSeoTitle = (companyName: string, tickerSymbol: string) => {
  const withName = `${shortCompanyName(companyName)} (${tickerSymbol}) Stock Price & Fundamentals`;
  return withName.length <= 52 ? withName : `${tickerSymbol} Stock Price & Fundamentals`;
};

// Converts a most-recent-first plain numeric array (the shape returned by
// the periods-style /financials endpoints) into a chronological series.
// The API does not return explicit dates for this shape, so year labels
// are approximated as trailing fiscal years ending at the current year.
const annualizeMostRecentFirst = (arr) => {
  if (!Array.isArray(arr) || arr.length === 0) return [];
  const lastYear = new Date().getFullYear();
  return arr
    .map((value, i) => ({ period: String(lastYear - i), value: Number(value) }))
    .filter((d) => Number.isFinite(d.value))
    .reverse();
};

const zipAnnualSeries = (aArr, bArr, aKey, bKey) => {
  const a = annualizeMostRecentFirst(aArr);
  const b = annualizeMostRecentFirst(bArr);
  const byPeriod = new Map();
  a.forEach((d) => byPeriod.set(d.period, { period: d.period, [aKey]: d.value }));
  b.forEach((d) => {
    const existing = byPeriod.get(d.period) || { period: d.period };
    existing[bKey] = d.value;
    byPeriod.set(d.period, existing);
  });
  return Array.from(byPeriod.values()).sort((x, y) => x.period.localeCompare(y.period));
};

const toDateSeries = (arr, valueKey) => {
  if (!Array.isArray(arr)) return [];
  return arr
    .map((d) => ({ __label: formatDateShort(d.reportDate), __value: Number(d[valueKey]), reportDate: d.reportDate }))
    .filter((d) => Number.isFinite(d.__value))
    .sort((x, y) => new Date(x.reportDate) - new Date(y.reportDate));
};

const mergeQuarterlySeries = (arrA, keyA, arrB, keyB) => {
  const byDate = new Map();
  (arrA || []).forEach((d) => {
    const key = String(d.reportDate).slice(0, 10);
    byDate.set(key, { ...(byDate.get(key) || {}), date: key, [keyA]: Number(d[keyA]) });
  });
  (arrB || []).forEach((d) => {
    const key = String(d.reportDate).slice(0, 10);
    byDate.set(key, { ...(byDate.get(key) || {}), date: key, [keyB]: Number(d[keyB]) });
  });
  return Array.from(byDate.values())
    .filter((d) => Number.isFinite(d[keyA]) || Number.isFinite(d[keyB]))
    .sort((x, y) => new Date(x.date) - new Date(y.date))
    .map((d) => ({ ...d, __label: formatDateShort(d.date) }));
};

const filterPricesByRange = (prices, range) => {
  if (!Array.isArray(prices) || prices.length === 0) return [];
  const sorted = [...prices].sort((a, b) => new Date(a.time) - new Date(b.time));
  if (range === 'MAX') return sorted;
  const years = { '1Y': 1, '5Y': 5, '10Y': 10 }[range] ?? 5;
  const cutoff = new Date();
  cutoff.setFullYear(cutoff.getFullYear() - years);
  return sorted.filter((p) => new Date(p.time) >= cutoff);
};

const buildFaqItems = (ticker) => [
  {
    q: `Does Axion offer a stock market API for ${ticker}?`,
    a: `Yes. Axion's stock data API returns real-time quotes, historical prices, fundamentals, SEC filings, ownership and sentiment for ${ticker} through a single financial data API key.`,
  },
  {
    q: `Can I pull ${ticker}'s historical market data programmatically?`,
    a: `Yes - the historical market data API returns daily, weekly, monthly, quarterly or yearly OHLCV bars for ${ticker}, with no fixed lookback limit.`,
  },
  {
    q: `Is ${ticker}'s fundamental data suitable for quant research?`,
    a: `Yes. Revenue, net income, free cash flow, ROE and DCF valuation are exposed as structured JSON, ready for a quantitative finance API or quant research API pipeline - no scraping or PDF parsing required.`,
  },
  {
    q: `Can I see which ETFs and funds hold ${ticker}?`,
    a: `Yes. Axion's institutional financial data API includes fund and ETF ownership, insider transactions and major-holder breakdowns for ${ticker}, refreshed alongside SEC filings.`,
  },
];

const buildCodeSnippets = (ticker, apiBaseUrl) => ({
  cURL: `curl ${apiBaseUrl}/stocks/${ticker}/quote \\\n  -H "Authorization: Bearer YOUR_API_KEY"`,
  Python: `from axion import Axion\n\nclient = Axion(api_key="YOUR_API_KEY")\n\nquote = client.stocks.quote("${ticker}")\nprint(quote["regularMarketPrice"])\n\nrevenue = client.financials.revenue("${ticker}", periods=8)\nprint(revenue)`,
  JavaScript: `import { Axion } from '@axionquant/sdk';\n\nconst client = new Axion('YOUR_API_KEY');\n\nconst quote = await client.stocks.quote('${ticker}');\nconsole.log(quote.regularMarketPrice);\n\nconst revenue = await client.financials.revenue('${ticker}', { periods: 8 });\nconsole.log(revenue);`,
});

const buildResearchExamples = (ticker, sector) => [
  {
    icon: FlaskConical,
    title: `Backtest a strategy on ${ticker}`,
    text: `Pull ${ticker}'s full daily price history and combine it with fundamentals to backtest momentum or mean-reversion signals.`,
  },
  {
    icon: BarChart3,
    title: 'Build a DCF valuation model',
    text: "Feed live revenue, free cash flow and discount-rate inputs into your own model, benchmarked against Axion's DCF fair value.",
  },
  {
    icon: Users,
    title: `Screen for ${sector || 'sector'} peers`,
    text: `Use supply-chain and sector classification data to build a comparable-company screen around ${ticker}.`,
  },
  {
    icon: Shield,
    title: 'Factor in ESG and sentiment',
    text: 'Combine ESG scores with news and social sentiment to test alternative-data factors against returns.',
  },
  {
    icon: UserCheck,
    title: 'Track insider conviction',
    text: 'Monitor insider buying and selling activity, plus ETF and institutional ownership shifts, ahead of catalysts.',
  },
  {
    icon: Landmark,
    title: 'Map supply-chain risk',
    text: `Trace ${ticker}'s suppliers and customers to model second-order exposure to sector shocks.`,
  },
];

const WHY_ITEMS = [
  {
    icon: Database,
    title: 'One API, every asset class',
    text: 'Equities, ETFs, crypto, forex and futures through a single financial data API - no juggling vendors.',
  },
  {
    icon: Landmark,
    title: 'Institutional-grade fundamentals',
    text: 'Point-in-time financial statements sourced from SEC filings, built for an institutional financial data API workflow.',
  },
  {
    icon: Rocket,
    title: 'Built for backtesting',
    text: 'Daily and weekly historical market data going back years, ready for a quantitative finance API pipeline.',
  },
  {
    icon: Sparkles,
    title: 'Alternative data included',
    text: 'ESG, sentiment, supply chain and insider activity - a financial intelligence API that goes beyond price.',
  },
  {
    icon: Code2,
    title: 'Free tier, real docs',
    text: 'Explore the stock data API for free - grab a key and start pulling market data in minutes.',
  },
];

const PRODUCTS = [
  { label: 'Stocks', desc: 'Price data & market details', href: 'https://axionquant.com/products/stocks' },
  { label: 'ETFs', desc: 'Holdings & constituent weights', href: 'https://axionquant.com/products/etfs' },
  { label: 'Forex', desc: 'Live currency pair rates', href: 'https://axionquant.com/products/forex' },
  { label: 'Futures', desc: 'Contract specs & pricing', href: 'https://axionquant.com/products/futures' },
  { label: 'Indices', desc: 'Global benchmarks & exposure', href: 'https://axionquant.com/products/indices' },
  { label: 'Crypto', desc: 'Digital asset price data', href: 'https://axionquant.com/products/crypto' },
  { label: 'Corporate Profiles', desc: 'Company identity & structure', href: 'https://axionquant.com/products/corporate-profiles' },
  { label: 'Earnings', desc: 'Reports & analyst estimates', href: 'https://axionquant.com/products/earnings' },
  { label: 'Disclosures', desc: 'Standardized filings & financial metrics', href: 'https://axionquant.com/products/disclosures' },
  { label: 'Macros', desc: 'Broad economic datasets', href: 'https://axionquant.com/products/economics' },
  { label: 'News', desc: 'Coverage by company & sector', href: 'https://axionquant.com/products/news' },
  { label: 'ESG', desc: 'Sustainability ratings', href: 'https://axionquant.com/products/esg' },
  { label: 'Credit', desc: 'Ratings across entity types', href: 'https://axionquant.com/products/credit' },
  { label: 'Supply Chain', desc: 'Peers, customers & rivals', href: 'https://axionquant.com/products/supply-chain' },
  { label: 'Sentiment', desc: 'Aggregated market mood', href: 'https://axionquant.com/products/sentiment' },
  { label: 'Web Traffic', desc: 'Digital presence & engagement', href: 'https://axionquant.com/products/webtraffic' },
];

const DEFAULT_TRUSTED_BY = [
  { name: 'Quant Funds' },
  { name: 'Prop Trading Desks' },
  { name: 'Fintech Startups' },
  { name: 'Academic Researchers' },
  { name: 'Independent Analysts' },
];

const SENTIMENT_COLOR = { POSITIVE: 'var(--positive)', NEGATIVE: 'var(--negative)', NEUTRAL: 'var(--subtext)' };
const C_ACCENT = 'var(--accent)';
const C_POSITIVE = 'var(--positive)';
const C_NEGATIVE = 'var(--negative)';
const C_MUTED = 'var(--hairline-strong)';
const AVATAR_PALETTE = ['#4d42d8', '#14bd6e', '#ff585a', '#8d8d8d', '#c9a227', '#1f7a8c'];

const hashTicker = (s) => {
  let h = 0;
  for (let i = 0; i < s.length; i += 1) h = (h * 31 + s.charCodeAt(i)) >>> 0;
  return h;
};

// ============================================================================
// Small presentational helpers (internal, not exported)
// ============================================================================

const TickerAvatar = ({ ticker, size = 36 }) => {
  const color = AVATAR_PALETTE[hashTicker(ticker || '') % AVATAR_PALETTE.length];
  const initials = (ticker || '').slice(0, 2).toUpperCase();
  return (
    <div className="ticker-avatar" style={{ width: size, height: size, background: color, fontSize: size * 0.4 }}>
      {initials}
    </div>
  );
};

const normalizeLogoSvg = (svg) => {
  const rootTagMatch = svg.match(/^<svg\b[^>]*>/i);
  if (!rootTagMatch) return svg;
  const rootTag = rootTagMatch[0];
  const attr = (name) => {
    const m = new RegExp(`\\s${name}=("[^"]*"|'[^']*')`, 'i').exec(rootTag);
    return m ? m[1].slice(1, -1) : null;
  };
  let updated = rootTag;
  if (!/\sviewBox=/i.test(updated)) {
    const w = attr('width');
    const h = attr('height');
    if (w && h && /^\d+(\.\d+)?$/.test(w.trim()) && /^\d+(\.\d+)?$/.test(h.trim())) {
      updated = updated.replace(/^<svg\b/i, `<svg viewBox="0 0 ${w.trim()} ${h.trim()}"`);
    }
  }
  updated = updated.replace(/\s(width|height)=("[^"]*"|'[^']*')/gi, '');
  return updated === rootTag ? svg : svg.replace(rootTag, updated);
};

const SectionEyebrow = ({ endpoint, label }) => (
  <div className="eyebrow">
    <Code2 size={13} aria-hidden="true" />
    <span className="eyebrow-label">{label}</span>
    {endpoint && <code className="eyebrow-endpoint">{endpoint}</code>}
  </div>
);

const StatCard = ({ icon: Icon, label, value }) => (
  <div className="stat-card">
    <div className="stat-card-head">
      {Icon && <Icon size={15} aria-hidden="true" />}
      <span>{label}</span>
    </div>
    <div className="stat-card-value">{value}</div>
  </div>
);

const ChartCard = ({ eyebrowLabel, endpoint, title, description, children }) => (
  <div className="chart-card">
    <SectionEyebrow endpoint={endpoint} label={eyebrowLabel} />
    <h3 className="chart-title">{title}</h3>
    {description && <p className="chart-desc">{description}</p>}
    <div className="chart-body">{children}</div>
  </div>
);

const EmptyNote = ({ children }) => <p className="empty-note">{children}</p>;

const ChartTooltip = ({ active, payload, label, formatter }) => {
  if (!active || !payload || !payload.length) return null;
  return (
    <div className="chart-tooltip">
      <div className="chart-tooltip-label">{label}</div>
      {payload.map((p) => (
        <div key={p.dataKey || p.name} className="chart-tooltip-row">
          <span className="chart-tooltip-dot" style={{ background: p.color }} />
          <span>{p.name}</span>
          <strong>{formatter ? formatter(p.value, p.name) : p.value}</strong>
        </div>
      ))}
    </div>
  );
};

const PriceTooltip = ({ active, payload, label, currency }) => {
  if (!active || !payload || !payload.length) return null;
  return (
    <div className="chart-tooltip">
      <div className="chart-tooltip-label">{label}</div>
      <div className="chart-tooltip-row">
        <strong>{formatPrice(payload[0].value, currency)}</strong>
      </div>
    </div>
  );
};

// Horizontal range/gauge indicator built from plain divs - used for DCF fair
// value and the 52-week range, where a stacked/floating recharts bar would
// be more code than it's worth for a single static row.
const RangeBar = ({ min = 0, max, segments = [], markers = [] }) => {
  const span = Math.max(max - min, 1e-9);
  const pct = (v) => Math.min(100, Math.max(0, ((v - min) / span) * 100));
  return (
    <div className="range-bar">
      <div className="range-bar-track">
        {segments.map((s, i) => (
          <div
            key={i}
            className={`range-bar-segment tone-${s.tone || 'accent'}`}
            style={{ left: `${pct(s.from)}%`, width: `${Math.max(pct(s.to) - pct(s.from), 1.5)}%` }}
          />
        ))}
        {markers.map((m, i) => (
          <div
            key={i}
            className={`range-bar-marker marker-${m.tone || 'ink'}`}
            style={{ left: `${pct(m.value)}%` }}
            title={m.label}
          />
        ))}
      </div>
      <div className="range-bar-scale">
        <span>{formatPrice(min)}</span>
        <span>{formatPrice(max)}</span>
      </div>
    </div>
  );
};

const SentimentCard = ({ icon: Icon, label, data, endpoint }) => {
  if (!data || !data.label) return null;
  const pct = Math.round((data.score || 0) * 100);
  const color = SENTIMENT_COLOR[data.label] || 'var(--subtext)';
  return (
    <div className="sentiment-card">
      <SectionEyebrow endpoint={endpoint} label="Sentiment API" />
      <div className="sentiment-head">
        <Icon size={16} aria-hidden="true" />
        <span>{label}</span>
      </div>
      <div className="sentiment-label" style={{ color }}>
        {data.label}
      </div>
      <div className="sentiment-meter">
        <div className="sentiment-meter-fill" style={{ width: `${pct}%`, background: color }} />
      </div>
      <div className="sentiment-score">{pct}% confidence</div>
    </div>
  );
};

const SupplyChainColumn = ({ icon: Icon, title, items }) => {
  if (!items || items.length === 0) return null;
  return (
    <div className="supply-column">
      <div className="supply-column-head">
        <Icon size={15} aria-hidden="true" />
        <span>{title}</span>
      </div>
      <ul>
        {items.slice(0, 6).map((it) => {
          const cap = parseMoneyString(it.marketCap);
          return (
            <li key={`${it.ticker || ''}-${it.companyName || it.name}`}>
              <div className="supply-row">
                <span className="supply-name">{it.companyName || it.name}</span>
                <span className="supply-ticker">{it.ticker}</span>
                {Number.isFinite(cap) && <span className="supply-cap">{compactCurrency(cap * 1e6)}</span>}
              </div>
            </li>
          );
        })}
      </ul>
    </div>
  );
};

const MoreLink = ({ href, children }) => (
  <div className="section-more-row">
    <a className="section-more" href={href} target="_blank" rel="noopener noreferrer">
      {children} <ArrowRight size={15} aria-hidden="true" />
    </a>
  </div>
);

// ============================================================================
// Styles
// ============================================================================
//
// Design language: the Axion landing-page system. A clean hairline
// masthead leads into serif section headings and numbered, captioned
// figures. Soft 1px card borders with 8px radii, pill-shaped badges and
// toggles, subtle hairline section dividers, and large serif headings
// mirror the rest of the landing page. Display and body type are
// Cormorant Garamond + DM Sans; a monospace face carries every number,
// label and endpoint, the way a terminal or footnote apparatus would.

const StyledTickerReport = styled.div`
  /* ---- tokens ---------------------------------------------------- */
  --bg: #ffffff;
  --surface: #f6f6f6;
  --surface-alt: #ffffff;
  --ink: #000000;
  --subtext: #8d8d8d;
  --hairline: rgba(141, 141, 141, 0.3);
  --hairline-strong: #e8e8e8;
  --accent: #4d42d8;
  --accent-soft: color-mix(in srgb, var(--accent) 10%, white);
  --positive: #14bd6e;
  --positive-soft: rgba(20, 189, 110, 0.1);
  --negative: #ff585a;
  --negative-soft: rgba(255, 88, 90, 0.1);
  --radius: 8px;
  --radius-lg: 10px;
  --font-display: 'Cormorant Garamond', Georgia, 'Times New Roman', serif;
  --font-body: 'DM Sans', 'Noto Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  --font-mono: 'Fragment Mono', 'IBM Plex Mono', 'JetBrains Mono', ui-monospace, SFMono-Regular, Menlo, Consolas,
    monospace;

  background: var(--bg);
  color: var(--ink);
  font-family: var(--font-body);
  -webkit-font-smoothing: antialiased;
  padding-top: 100px;
  * {
    box-sizing: border-box;
  }

  a {
    color: inherit;
  }

  h3{
    padding-bottom: 12px;
  }

  .report {
    max-width: 1180px;
    margin: 0 auto;
    padding: 0 40px 64px;
    counter-reset: fig;
  }

  a:focus-visible,
  button:focus-visible,
  summary:focus-visible,
  input:focus-visible {
    outline: 2px solid var(--accent);
    outline-offset: 2px;
    border-radius: 0;
  }

  /* ---- loading / not-found states --------------------------------- */
  .skeleton,
  .not-found {
    max-width: 720px;
    margin: 0 auto;
    padding: 96px 20px;
    text-align: center;
    color: var(--subtext);
    font-size: 15px;
    font-family: var(--font-mono);
  }
  .not-found {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 12px;
    font-family: var(--font-body);
  }
  .not-found svg {
    color: var(--negative);
  }

  /* ---- masthead -------------------------------------------------------- */
  .masthead {
    display: flex;
    justify-content: space-between;
    align-items: baseline;
    flex-wrap: wrap;
    gap: 8px 20px;
    padding: 24px 0 16px;
    margin-bottom: 48px;
    border-bottom: 1px solid var(--hairline);
  }
  .masthead-brand {
    display: inline-flex;
    align-items: center;
    gap: 7px;
    font-family: var(--font-mono);
    font-size: 12px;
    font-weight: 700;
    letter-spacing: 0.09em;
    text-transform: uppercase;
    color: var(--ink);
  }
  .masthead-brand svg {
    color: var(--accent);
  }
  .masthead-meta {
    font-family: var(--font-mono);
    font-size: 12px;
    letter-spacing: 0.03em;
    color: var(--subtext);
  }

  /* ---- eyebrow / endpoint caption -------------------------------------- */
  .eyebrow {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 8px;
    margin-bottom: 12px;
    color: var(--subtext);
  }
  .eyebrow-label {
    font-family: var(--font-mono);
    font-size: 11px;
    letter-spacing: 0.07em;
    text-transform: uppercase;
    font-weight: 600;
  }
  .eyebrow-endpoint {
    font-family: var(--font-mono);
    font-size: 11px;
    background: var(--surface);
    border: 1px solid var(--hairline-strong);
    border-radius: 6px;
    padding: 3px 8px;
    color: var(--ink);
  }
  .eyebrow-endpoint::before {
    content: '[ ';
    color: var(--hairline-strong);
  }
  .eyebrow-endpoint::after {
    content: ' ]';
    color: var(--hairline-strong);
  }

  /* ---- hero --------------------------------------------------------- */
  .hero {
    padding: 40px 0 48px;
    border-bottom: 1px solid var(--hairline);
    margin-bottom: 64px;
  }
  .hero-top {
    display: grid;
    grid-template-columns: 1fr;
    gap: 32px;
  }
  .hero-name-row {
    display: flex;
    align-items: center;
    gap: 16px;
    margin-bottom: 14px;
  }
  .hero-logo {
    width: 56px;
    height: 56px;
    object-fit: contain;
    border-radius: 8px;
    border: 1px solid var(--hairline-strong);
    background: #fff;
    padding: 8px;
    flex-shrink: 0;
  }
  .hero-logo-inline {
    display: flex;
    align-items: center;
    justify-content: center;
    overflow: hidden;
  }
  .hero-logo-inline svg {
    width: 100%;
    height: 100%;
    display: block;
  }
  .hero-badge-row {
    display: flex;
    gap: 10px;
    flex-wrap: wrap;
    margin-bottom: 18px;
  }
  .exchange-badge,
  .type-badge {
    font-family: var(--font-body);
    font-size: 12px;
    font-weight: 600;
    letter-spacing: 0.02em;
    text-transform: uppercase;
    padding: 6px 13px;
    border-radius: 999px;
    display: inline-flex;
    align-items: center;
    gap: 6px;
  }
  .exchange-badge {
    background: var(--ink);
    color: var(--bg);
  }
  .type-badge {
    background: transparent;
    color: var(--accent);
    border: 1px solid var(--accent);
  }
  h1 {
    font-family: var(--font-display);
    font-size: clamp(34px, 4.5vw, 52px);
    line-height: 1.08;
    letter-spacing: -0.01em;
    margin: 0;
    font-weight: 600;
  }
  h1 .ticker-symbol {
    color: var(--subtext);
    font-family: var(--font-mono);
    font-weight: 500;
    font-size: 0.45em;
  }
  .hero-tagline {
    font-family: var(--font-display);
    font-size: 19px;
    line-height: 1.6;
    color: var(--subtext);
    max-width: 62ch;
    margin: 16px 0 18px;
  }
  .pill-row {
    display: flex;
    flex-wrap: wrap;
    gap: 10px;
    margin-bottom: 8px;
  }
  .pill {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    font-family: var(--font-body);
    font-size: 12.5px;
    padding: 6px 13px;
    background: var(--surface);
    border: 1px solid var(--hairline-strong);
    border-radius: 999px;
    color: var(--ink);
  }
  .detail-row {
    display: flex;
    flex-wrap: wrap;
    gap: 16px;
    margin: 12px 0 20px;
  }
  .detail-item {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    font-size: 13px;
    color: var(--subtext);
    text-decoration: none;
  }
  a.detail-item:hover {
    color: var(--accent);
  }
  .hero-description {
    font-family: var(--font-display);
    font-size: 17px;
    line-height: 1.7;
    color: var(--subtext);
    max-width: 70ch;
    margin: 0;
  }

  .hero-price {
    background: var(--surface-alt);
    border: 1px solid var(--hairline-strong);
    border-radius: var(--radius);
    padding: 28px;
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.04);
    display: flex;
    flex-direction: column;
    gap: 14px;
    align-self: start;
  }
  .price-label {
    display: block;
    font-family: var(--font-mono);
    font-size: 11px;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    color: var(--subtext);
    margin-bottom: 6px;
  }
  .price-value {
    font-family: var(--font-mono);
    font-size: 40px;
    font-weight: 600;
    font-variant-numeric: tabular-nums;
    letter-spacing: -0.01em;
  }
  .price-change {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    font-family: var(--font-mono);
    font-size: 13px;
    font-weight: 600;
    width: fit-content;
    padding: 5px 11px;
    border-radius: 999px;
    margin-top: 10px;
  }
  .price-change.positive {
    color: var(--positive);
    background: var(--positive-soft);
  }
  .price-change.negative {
    color: var(--negative);
    background: var(--negative-soft);
  }
  .hero-ctas {
    display: flex;
    flex-wrap: wrap;
    gap: 12px;
  }
  .btn {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    font-family: var(--font-body);
    font-size: 15px;
    font-weight: 600;
    letter-spacing: 0;
    text-transform: none;
    padding: 12px 22px;
    border-radius: var(--radius);
    text-decoration: none;
    border: 1px solid var(--ink);
    cursor: pointer;
    transition: background 0.15s ease, color 0.15s ease;
  }
  .btn-primary {
    background: var(--ink);
    color: var(--bg);
  }
  .btn-primary:hover {
    background: #333;
    color: var(--bg);
  }
  .btn-ghost {
    background: transparent;
    color: var(--ink);
    border-color: var(--hairline-strong);
  }
  .btn-ghost:hover {
    background: #eee;
    color: var(--ink);
  }
  .btn-text {
    background: transparent;
    color: var(--subtext);
    padding: 10px 4px;
    border: none;
    text-transform: none;
    letter-spacing: 0;
    font-weight: 600;
    font-size: 14px;
  }
  .btn-text:hover {
    color: var(--accent);
  }

  .hero-stats {
    margin-top: 32px;
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
    gap: 12px;
  }
  .stat-card {
    background: var(--surface-alt);
    border: 1px solid var(--hairline-strong);
    border-radius: var(--radius);
    padding: 18px 20px;
  }
  .stat-card-head {
    display: flex;
    align-items: center;
    gap: 6px;
    font-family: var(--font-mono);
    font-size: 10.5px;
    color: var(--subtext);
    text-transform: uppercase;
    letter-spacing: 0.06em;
    margin-bottom: 8px;
  }
  .stat-card-value {
    font-family: var(--font-mono);
    font-size: 19px;
    font-weight: 600;
    font-variant-numeric: tabular-nums;
  }
  .margin-stats {
    margin: 0 0 20px;
  }

  /* ---- data-coverage ledger ------------------------------------------- */
  .ledger {
    margin-bottom: 64px;
    padding: 28px 30px;
    background: var(--surface);
    border: 1px solid var(--hairline-strong);
    border-radius: var(--radius-lg);
  }
  .ledger-head {
    display: flex;
    align-items: center;
    gap: 8px;
    font-family: var(--font-mono);
    font-size: 13px;
    letter-spacing: 0.02em;
    font-weight: 600;
    margin-bottom: 16px;
  }
  .ledger-list {
    list-style: none;
    margin: 0;
    padding: 0;
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
    gap: 10px 18px;
  }
  .ledger-list li {
    display: flex;
    align-items: center;
    gap: 8px;
    font-family: var(--font-mono);
    font-size: 12px;
  }
  .ledger-list li.available {
    color: var(--ink);
  }
  .ledger-list li.available svg {
    color: var(--positive);
    flex-shrink: 0;
  }
  .ledger-list li.unavailable {
    color: var(--subtext);
  }
  .ledger-list li.unavailable svg {
    flex-shrink: 0;
  }

  /* ---- generic section scaffolding ---------------------------------- */
  .section {
    margin-bottom: 80px;
  }
  .section-head {
    margin-bottom: 32px;
    max-width: 74ch;
    padding-top: 32px;
    border-top: 1px solid var(--hairline);
  }
  .section-head h2 {
    font-family: var(--font-display);
    font-size: clamp(30px, 3.2vw, 40px);
    letter-spacing: -0.01em;
    margin: 0 0 12px;
    font-weight: 600;
  }
  .section-head p {
    font-family: var(--font-display);
    font-size: 17px;
    line-height: 1.7;
    color: var(--subtext);
    margin: 0;
  }
  .section-head-row {
    display: flex;
    justify-content: space-between;
    align-items: flex-end;
    gap: 20px;
    flex-wrap: wrap;
  }

  /* ---- "see more" link after data blocks ------------------------------ */
  .section-more-row {
    display: flex;
    justify-content: flex-end;
    margin-top: 24px;
  }
  .section-more {
    display: inline-flex;
    align-items: center;
    gap: 7px;
    font-family: var(--font-body);
    font-size: 14px;
    font-weight: 600;
    color: var(--ink);
    text-decoration: none;
    border: 1px solid var(--hairline-strong);
    border-radius: 999px;
    padding: 9px 18px;
    transition: background 0.15s ease, color 0.15s ease, border-color 0.15s ease;
  }
  .section-more:hover {
    background: var(--ink);
    color: var(--bg);
    border-color: var(--ink);
  }
  .section-more svg {
    transition: transform 0.15s ease;
  }
  .section-more:hover svg {
    transform: translateX(2px);
  }

  /* ---- why-axion grid --------------------------------------------- */
  .why-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
    gap: 16px;
  }
  .why-card {
    background: var(--surface-alt);
    border: 1px solid var(--hairline-strong);
    border-radius: var(--radius);
    padding: 24px;
  }
  .why-card-icon {
    width: 36px;
    height: 36px;
    border-radius: 8px;
    background: var(--accent-soft);
    color: var(--accent);
    display: flex;
    align-items: center;
    justify-content: center;
    margin-bottom: 14px;
  }
  .why-card h3 {
    font-family: var(--font-display);
    font-size: 18px;
    margin: 0 0 6px;
    font-weight: 600;
  }
  .why-card p {
    font-size: 13.5px;
    color: var(--subtext);
    line-height: 1.6;
    margin: 0;
  }

  /* ---- range toggle (interactive price chart) ------------------------ */
  .range-toggle {
    display: inline-flex;
    gap: 8px;
    margin-bottom: 20px;
  }
  .range-toggle button {
    font-family: var(--font-body);
    font-size: 13px;
    font-weight: 600;
    letter-spacing: 0;
    border: 1px solid var(--hairline-strong);
    background: transparent;
    color: var(--subtext);
    padding: 7px 16px;
    border-radius: 999px;
    cursor: pointer;
    transition: background 0.12s ease, color 0.12s ease;
  }
  .range-toggle button:hover {
    color: var(--ink);
    border-color: var(--ink);
  }
  .range-toggle button.active {
    background: var(--ink);
    color: var(--bg);
    border-color: var(--ink);
  }

  /* ---- chart cards ---------------------------------------------------- */
  .chart-grid {
    display: grid;
    grid-template-columns: 1fr;
    gap: 24px;
  }
  .chart-grid.two-col {
    grid-template-columns: repeat(2, 1fr);
  }
  .chart-card {
    counter-increment: fig;
    background: var(--surface-alt);
    border: 1px solid var(--hairline-strong);
    border-radius: var(--radius);
    padding: 28px;
  }
  .chart-title {
    font-family: var(--font-display);
    font-size: 20px;
    font-weight: 600;
    margin: 0 0 4px;
  }
  .chart-title::before {
    content: 'Fig. ' counter(fig, decimal-leading-zero) '\\2014';
    font-family: var(--font-mono);
    font-size: 0.6em;
    font-weight: 700;
    color: var(--accent);
    letter-spacing: 0.02em;
    margin-right: 6px;
  }
  .chart-desc {
    font-family: var(--font-display);
    font-style: italic;
    font-size: 14px;
    color: var(--subtext);
    margin: 0 0 16px;
    line-height: 1.55;
  }
  .chart-body {
    margin-top: 4px;
  }
  .empty-note {
    font-size: 14px;
    color: var(--subtext);
    font-style: italic;
    font-family: var(--font-display);
  }

  .chart-tooltip {
    background: var(--ink);
    color: var(--bg);
    padding: 10px 14px;
    border-radius: 8px;
    font-size: 12.5px;
  }
  .chart-tooltip-label {
    font-family: var(--font-mono);
    opacity: 0.7;
    margin-bottom: 4px;
  }
  .chart-tooltip-row {
    display: flex;
    align-items: center;
    gap: 6px;
    font-variant-numeric: tabular-nums;
    font-family: var(--font-mono);
  }
  .chart-tooltip-dot {
    width: 7px;
    height: 7px;
    border-radius: 50%;
    display: inline-block;
  }

  /* ---- valuation / range bars ------------------------------------------ */
  .valuation-grid {
    display: grid;
    grid-template-columns: 1fr;
    gap: 24px;
    margin-bottom: 32px;
  }
  .valuation-card {
    background: var(--surface-alt);
    border: 1px solid var(--hairline-strong);
    border-radius: var(--radius);
    padding: 26px;
  }
  .valuation-badge {
    display: inline-block;
    font-family: var(--font-body);
    font-size: 12px;
    font-weight: 600;
    letter-spacing: 0.03em;
    text-transform: uppercase;
    padding: 6px 14px;
    border-radius: 999px;
    border: 1px solid currentColor;
    margin-bottom: 16px;
  }
  .valuation-badge.buy {
    background: var(--positive-soft);
    color: var(--positive);
  }
  .valuation-badge.sell {
    background: var(--negative-soft);
    color: var(--negative);
  }
  .valuation-row {
    display: flex;
    gap: 32px;
    margin-bottom: 18px;
    flex-wrap: wrap;
  }
  .valuation-row .label {
    display: block;
    font-family: var(--font-mono);
    font-size: 10.5px;
    color: var(--subtext);
    text-transform: uppercase;
    letter-spacing: 0.06em;
    margin-bottom: 4px;
  }
  .valuation-row .value {
    display: block;
    font-family: var(--font-mono);
    font-size: 22px;
    font-weight: 600;
  }
  .margin-of-safety {
    font-family: var(--font-display);
    font-size: 14px;
    color: var(--subtext);
    margin: 16px 0 0;
  }

  .range-bar {
    margin-top: 8px;
  }
  .range-bar-track {
    position: relative;
    height: 8px;
    background: var(--surface);
    border: 1px solid var(--hairline-strong);
    border-radius: 999px;
    overflow: visible;
  }
  .range-bar-segment {
    position: absolute;
    top: 0;
    height: 100%;
    border-radius: 999px;
  }
  .range-bar-segment.tone-accent {
    background: var(--accent);
  }
  .range-bar-segment.tone-positive {
    background: var(--positive);
  }
  .range-bar-segment.tone-negative {
    background: var(--negative);
  }
  .range-bar-marker {
    position: absolute;
    top: -5px;
    width: 2px;
    height: 18px;
    transform: translateX(-50%);
    border-radius: 2px;
  }
  .range-bar-marker.marker-ink {
    background: var(--ink);
  }
  .range-bar-marker.marker-accent-strong {
    background: var(--accent);
    width: 3px;
  }
  .range-bar-scale {
    display: flex;
    justify-content: space-between;
    font-family: var(--font-mono);
    font-size: 11px;
    color: var(--subtext);
    margin-top: 6px;
  }

  /* ---- searchable tables ------------------------------------------------ */
  .table-toolbar {
    display: flex;
    align-items: center;
    gap: 8px;
    background: var(--surface-alt);
    border: 1px solid var(--hairline-strong);
    border-radius: var(--radius);
    padding: 10px 16px;
    margin-bottom: 16px;
    max-width: 380px;
  }
  .table-toolbar input {
    border: none;
    background: transparent;
    outline: none;
    font-size: 14px;
    width: 100%;
    font-family: var(--font-body);
    color: var(--ink);
  }
  .table-toolbar svg {
    color: var(--subtext);
    flex-shrink: 0;
  }
  .data-table-wrap {
    overflow-x: auto;
    background: var(--surface-alt);
    border: 1px solid var(--hairline-strong);
    border-radius: var(--radius);
    padding: 6px 24px;
  }
  .data-table {
    width: 100%;
    border-collapse: collapse;
    font-size: 13px;
  }
  .data-table th {
    text-align: left;
    font-family: var(--font-mono);
    font-size: 10.5px;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    color: var(--subtext);
    padding: 14px 10px 10px 0;
    border-bottom: 1px solid var(--hairline-strong);
  }
  .data-table td {
    padding: 11px 10px 11px 0;
    border-bottom: 1px solid var(--hairline);
    font-family: var(--font-mono);
    font-variant-numeric: tabular-nums;
  }
  .data-table tr:last-child td {
    border-bottom: none;
  }

  /* ---- related companies -------------------------------------------------- */
  .related-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
    gap: 12px;
    margin-bottom: 24px;
  }
  .related-card {
    display: flex;
    align-items: center;
    gap: 12px;
    background: var(--surface-alt);
    border: 1px solid var(--hairline-strong);
    border-radius: var(--radius);
    padding: 16px 18px;
    text-decoration: none;
    color: var(--ink);
    transition: background 0.12s ease;
  }
  .related-card:hover {
    background: var(--surface);
  }
  .related-card > svg:last-child {
    margin-left: auto;
    color: var(--subtext);
    flex-shrink: 0;
  }
  .related-card > div {
    flex: 1;
    min-width: 0;
  }
  .related-name {
    font-weight: 600;
    font-size: 14px;
    line-height: 1.35;
  }
  .related-ticker {
    font-family: var(--font-mono);
    font-size: 11.5px;
    color: var(--subtext);
  }

  /* ---- supply chain ------------------------------------------------------ */
  .supply-chain-grid {
    display: grid;
    grid-template-columns: 1fr;
    gap: 12px;
  }
  .supply-column {
    background: var(--surface-alt);
    border: 1px solid var(--hairline-strong);
    border-radius: var(--radius);
    padding: 22px;
  }
  .supply-column-head {
    display: flex;
    align-items: center;
    gap: 7px;
    font-family: var(--font-display);
    font-weight: 600;
    font-size: 17px;
    margin-bottom: 14px;
  }
  .supply-column ul {
    list-style: none;
    margin: 0;
    padding: 0;
    display: flex;
    flex-direction: column;
    gap: 10px;
  }
  .supply-column li {
    padding-bottom: 8px;
    border-bottom: 1px solid var(--hairline);
    font-size: 13px;
  }
  .supply-column li:last-child {
    border-bottom: none;
    padding-bottom: 0;
  }
  .supply-row {
    display: flex;
    align-items: baseline;
    gap: 8px;
    width: 100%;
    text-decoration: none;
    color: inherit;
  }
  .supply-name {
    flex: 1;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  .supply-ticker {
    font-family: var(--font-mono);
    color: var(--subtext);
    font-size: 11.5px;
  }
  .supply-cap {
    font-family: var(--font-mono);
    font-weight: 600;
    font-size: 12px;
  }

  /* ---- ownership -------------------------------------------------------- */
  .ownership-card {
    background: var(--surface-alt);
    border: 1px solid var(--hairline-strong);
    border-radius: var(--radius);
    padding: 26px;
  }
  .ownership-flex {
    display: grid;
    grid-template-columns: 1fr;
    gap: 24px;
    align-items: center;
  }
  .mini-table {
    width: 100%;
    border-collapse: collapse;
    font-size: 12.5px;
    font-family: var(--font-mono);
  }
  .mini-table th {
    text-align: left;
    color: var(--subtext);
    font-weight: 600;
    text-transform: uppercase;
    font-size: 10.5px;
    letter-spacing: 0.05em;
    padding: 0 8px 8px 0;
    border-bottom: 1px solid var(--hairline-strong);
  }
  .mini-table td {
    padding: 8px 8px 8px 0;
    border-bottom: 1px solid var(--hairline);
    vertical-align: top;
  }
  .mini-table tr:last-child td {
    border-bottom: none;
  }

  /* ---- sentiment ---------------------------------------------------------- */
  .sentiment-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(230px, 1fr));
    gap: 12px;
  }
  .sentiment-card {
    background: var(--surface-alt);
    border: 1px solid var(--hairline-strong);
    border-radius: var(--radius);
    padding: 22px;
  }
  .sentiment-head {
    display: flex;
    align-items: center;
    gap: 7px;
    font-family: var(--font-mono);
    font-size: 12px;
    text-transform: uppercase;
    letter-spacing: 0.03em;
    color: var(--subtext);
    margin-bottom: 8px;
  }
  .sentiment-label {
    font-family: var(--font-display);
    font-size: 21px;
    font-weight: 700;
    margin-bottom: 10px;
  }
  .sentiment-meter {
    height: 5px;
    background: var(--surface);
    border: 1px solid var(--hairline-strong);
    border-radius: 999px;
    overflow: hidden;
    margin-bottom: 6px;
  }
  .sentiment-meter-fill {
    height: 100%;
  }
  .sentiment-score {
    font-size: 11.5px;
    color: var(--subtext);
    font-family: var(--font-mono);
  }

  /* ---- filings / news --------------------------------------------------- */
  .two-col-list {
    display: grid;
    grid-template-columns: 1fr;
    gap: 32px;
  }
  .two-col-list h3 {
    font-family: var(--font-display);
    font-size: 19px;
    font-weight: 600;
    margin: 2px 0 16px;
  }
  .link-list {
    list-style: none;
    margin: 0;
    padding: 0;
    display: flex;
    flex-direction: column;
    gap: 12px;
  }
  .link-list li {
      display: flex;
      align-items: baseline;
      gap: 10px;
      padding-bottom: 12px;
      border-bottom: 1px solid var(--hairline);
      font-size: 14px;
    }
    .link-list.row li {
      flex-direction: column;
    }
    .link-list li:last-child {
      border-bottom: none;
      padding-bottom: 0;
    }
    .link-list a {
      text-decoration: none;
      display: inline-flex;
      align-items: center;
      gap: 5px;
      flex: 1;
    }
    .link-list a:hover {
      text-decoration: underline;
    }
    .link-list a svg {
      flex-shrink: 0;
      opacity: 0.5;
    }
    .form-badge {
      font-family: var(--font-mono);
      font-size: 10.5px;
      font-weight: 700;
      background: var(--surface);
      border: 1px solid var(--hairline-strong);
      padding: 3px 8px;
      border-radius: 6px;
      flex-shrink: 0;
    }
    .news-date {
      font-family: var(--font-mono);
      font-size: 11px;
      color: var(--subtext);
      white-space: nowrap;
    }

    /* ---- code examples ------------------------------------------------------ */
    .example-grid {
      display: grid;
      grid-template-columns: 1fr;
      gap: 24px;
      align-items: start;
    }
    .code-tabs {
      display: flex;
      flex-wrap: wrap;
      padding-bottom: 12px;
    gap: 8px;
  }
  .code-tab {
    font-family: var(--font-body);
    font-size: 13px;
    font-weight: 600;
    padding: 8px 16px;
    border: 1px solid var(--hairline-strong);
    background: transparent;
    color: var(--subtext);
    cursor: pointer;
    border-radius: 999px;
  }
  .code-tab:hover {
    color: var(--ink);
    border-color: var(--ink);
  }
  .code-tab.active {
    background: var(--ink);
    color: var(--bg);
    border-color: var(--ink);
  }
  .code-panel {
    position: relative;
    background: var(--surface);
    border: 1px solid var(--hairline-strong);
    border-radius: var(--radius-lg);
    padding: 24px;
  }
  .code-panel pre {
    margin: 0;
    font-family: var(--font-mono);
    font-size: 13px;
    line-height: 1.6;
    overflow-x: auto;
    color: var(--ink);
    white-space: pre-wrap;
    word-break: break-word;
  }
  .copy-btn {
    position: absolute;
    top: 14px;
    right: 14px;
    display: flex;
    align-items: center;
    gap: 5px;
    font-size: 11px;
    font-family: var(--font-mono);
    background: var(--surface-alt);
    border: 1px solid var(--hairline-strong);
    color: var(--subtext);
    padding: 5px 10px;
    border-radius: 6px;
    cursor: pointer;
  }
  .copy-btn:hover {
    color: var(--ink);
    border-color: var(--ink);
  }
  .example-chart-card {
    background: var(--surface-alt);
    border: 1px solid var(--hairline-strong);
    border-radius: var(--radius);
    padding: 22px;
  }

  /* ---- research examples -------------------------------------------------- */
  .research-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
    gap: 12px;
  }
  .research-card {
    background: var(--surface-alt);
    border: 1px solid var(--hairline-strong);
    border-radius: var(--radius);
    padding: 22px;
  }
  .research-card svg {
    color: var(--accent);
    margin-bottom: 12px;
  }
  .research-card h3 {
    font-size: 16px;
    font-weight: 600;
    margin: 0 0 8px;
    font-family: var(--font-display);
  }
  .research-card p {
    font-size: 13px;
    color: var(--subtext);
    line-height: 1.6;
    margin: 0;
  }

  /* ---- products grid -------------------------------------------------------- */
  .products-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(210px, 1fr));
    gap: 12px;
  }
  .product-card {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
    padding: 16px 18px;
    background: var(--surface-alt);
    border: 1px solid var(--hairline-strong);
    border-radius: var(--radius);
    text-decoration: none;
    color: var(--ink);
    transition: background 0.12s ease;
  }
  .product-card:hover {
    background: var(--surface);
  }
  .product-card-label {
    font-weight: 650;
    font-size: 14px;
  }
  .product-card-desc {
    font-size: 12px;
    color: var(--subtext);
  }
  .product-card svg {
    color: var(--subtext);
    flex-shrink: 0;
  }

  /* ---- trusted-by ------------------------------------------------------------ */
  .trusted-grid {
    display: flex;
    flex-wrap: wrap;
    gap: 10px;
  }
  .trusted-badge {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    font-family: var(--font-body);
    font-size: 12px;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.03em;
    padding: 9px 16px;
    border: 1px solid var(--hairline-strong);
    border-radius: 999px;
    color: var(--subtext);
  }
  .trusted-logo {
    height: 22px;
    width: auto;
    object-fit: contain;
  }

  /* ---- SEO copy + FAQ ------------------------------------------------------ */
  .seo-copy {
    margin: 8px 0 64px;
    padding-top: 40px;
    border-top: 1px solid var(--hairline);
    max-width: 78ch;
  }
  .seo-copy h2 {
    font-family: var(--font-display);
    font-size: clamp(28px, 3.2vw, 38px);
    margin: 0 0 18px;
    font-weight: 600;
  }
  .seo-copy p {
    font-family: var(--font-display);
    font-size: 17px;
    line-height: 1.75;
    color: var(--subtext);
    margin: 0 0 20px;
  }
  .faq {
    margin-top: 32px;
  }
  .faq h3 {
    font-family: var(--font-display);
    font-size: 20px;
    font-weight: 600;
    margin: 0 0 16px;
  }
  .faq details {
    border-bottom: 1px solid var(--hairline-strong);
    padding: 16px 0;
  }
  .faq summary {
    cursor: pointer;
    font-family: var(--font-body);
    font-weight: 600;
    font-size: 15.5px;
    list-style: none;
    display: flex;
    justify-content: space-between;
    gap: 12px;
  }
  .faq summary::-webkit-details-marker {
    display: none;
  }
  .faq summary::after {
    content: '+';
    font-family: var(--font-mono);
    color: var(--subtext);
    flex-shrink: 0;
  }
  .faq details[open] summary::after {
    content: '-';
  }
  .faq p {
    margin: 10px 0 0;
    font-size: 14.5px;
    line-height: 1.65;
  }

  /* ---- final CTA -------------------------------------------------------- */
  .final-cta {
    background: var(--surface);
    border: 1px solid var(--hairline-strong);
    border-radius: var(--radius-lg);
    padding: 48px;
    display: grid;
    grid-template-columns: 1fr;
    gap: 28px;
    align-items: center;
  }
  .final-cta-copy svg {
    color: var(--accent);
    margin-bottom: 12px;
  }
  .final-cta h2 {
    font-family: var(--font-display);
    font-size: clamp(28px, 3.2vw, 38px);
    margin: 0 0 12px;
    font-weight: 600;
  }
  .final-cta p {
    font-family: var(--font-display);
    font-size: 17px;
    line-height: 1.7;
    color: var(--subtext);
    margin: 0 0 22px;
    max-width: 52ch;
  }
  .final-cta .btn-primary {
    background: var(--ink);
    color: var(--bg);
    border-color: var(--ink);
  }
  .final-cta .btn-primary:hover {
    background: #333;
    color: var(--bg);
  }
  .final-cta .btn-ghost {
    color: var(--ink);
    border-color: var(--hairline-strong);
    background: transparent;
  }
  .final-cta .btn-ghost:hover {
    background: #eee;
    color: var(--ink);
  }
  .code-snippet {
    background: var(--surface-alt);
    border: 1px solid var(--hairline-strong);
    border-radius: var(--radius-lg);
    padding: 20px;
    font-family: var(--font-mono);
    font-size: 13px;
    line-height: 1.6;
    overflow-x: auto;
    color: var(--ink);
    margin: 0;
  }

  /* ---- footer disclaimer -------------------------------------------------- */
  .disclaimer {
    display: flex;
    align-items: flex-start;
    gap: 8px;
    margin-top: 40px;
    padding-top: 20px;
    border-top: 1px solid var(--hairline);
    font-family: var(--font-display);
    font-style: italic;
    font-size: 13px;
    line-height: 1.6;
    color: var(--subtext);
  }
  .disclaimer svg {
    flex-shrink: 0;
    margin-top: 2px;
    font-style: normal;
  }

  /* ---- ticker avatar ------------------------------------------------------ */
  .ticker-avatar {
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    border-radius: 8px;
    color: #fff;
    font-family: var(--font-mono);
    font-weight: 700;
    flex: none!important;
    letter-spacing: 0.02em;
  }

  /* ---- breakpoints -------------------------------------------------------- */
  @media (min-width: 900px) {
    .hero-top {
      grid-template-columns: 1.6fr 1fr;
    }
    .hero-price {
      align-self: flex-start;
    }
    .valuation-grid {
      grid-template-columns: repeat(2, 1fr);
    }
    .supply-chain-grid {
      grid-template-columns: repeat(3, 1fr);
    }
    .two-col-list {
      grid-template-columns: 1fr 1fr;
    }
    .final-cta {
      grid-template-columns: 1.1fr 1fr;
    }
    .ownership-flex {
      grid-template-columns: 220px 1fr;
    }
    .example-grid {
      grid-template-columns: 1.15fr 0.85fr;
    }
  }

  @media (max-width: 899px) {
    .chart-grid.two-col {
      grid-template-columns: 1fr;
    }
  }

  @media (max-width: 560px) {
    .report {
      padding: 0 20px 48px;
    }
    .chart-card,
    .valuation-card,
    .ownership-card,
    .final-cta,
    .data-table-wrap {
      padding: 16px;
    }
    .price-value {
      font-size: 28px;
    }
  }
`;

// ============================================================================
// Main component
// ============================================================================

const AxionTickerReport = ({
  ticker,
  initialData = null,
  apiBaseUrl = DEFAULT_API_BASE,
  accent = '#4d42d8',
  signupUrl = 'https://axionquant.com/login',
  docsUrl = 'https://axionquant.com/docs',
  blogUrl = 'https://axionquant.com/blog/',
  pricingUrl = 'https://axionquant.com/pricing',
  trustedByLogos = null,
  className = undefined,
}) => {
  const data = initialData;
  const [range, setRange] = useState('5Y');
  const [fundSearch, setFundSearch] = useState('');
  const [activeTab, setActiveTab] = useState('cURL');
  const [copied, setCopied] = useState(false);
  const [logoError, setLogoError] = useState(false);

  const inlineLogoSvg = data?.logoSvg ? normalizeLogoSvg(data.logoSvg) : null;

  const priceSeries = useMemo(
    () =>
      filterPricesByRange(data?.prices, range).map((p) => ({
        __label: formatDateShort(p.time),
        close: Number(p.close),
      })),
    [data?.prices, range]
  );

  const revenueNetIncome = useMemo(() => zipAnnualSeries(data?.revenue, data?.netincome, 'revenue', 'netincome'), [
    data?.revenue,
    data?.netincome,
  ]);
  const fcfCapex = useMemo(() => zipAnnualSeries(data?.fcf, data?.capex, 'fcf', 'capex'), [data?.fcf, data?.capex]);
  const epsSeries = useMemo(() => toDateSeries(data?.eps, 'tailingEps'), [data?.eps]);
  const roeDe = useMemo(() => mergeQuarterlySeries(data?.roe, 'roe', data?.de, 'debtToEquity'), [
    data?.roe,
    data?.de,
  ]);
  const recommendationTrend = useMemo(() => (data?.recommendation || []).slice(0, 4).slice().reverse(), [
    data?.recommendation,
  ]);

  const ownershipData = useMemo(() => {
    const ownership = data?.ownership;
    if (!ownership) return [];
    const inst = (ownership.institutionsPercentHeld || 0) * 100;
    const ins = (ownership.insidersPercentHeld || 0) * 100;
    const pub = Math.max(0, 100 - inst - ins);
    return [
      { name: 'Institutions', value: Number(inst.toFixed(1)) },
      { name: 'Insiders', value: Number(ins.toFixed(1)) },
      { name: 'Public / Other', value: Number(pub.toFixed(1)) },
    ];
  }, [data?.ownership]);

  const esgData = useMemo(() => {
    const order = ['esg', 'environment', 'social', 'governance', 'controversy'];
    return (data?.esg || []).slice().sort((a, b) => order.indexOf(a.category) - order.indexOf(b.category));
  }, [data?.esg]);

  const filteredFunds = useMemo(() => {
    const q = fundSearch.trim().toLowerCase();
    const list = data?.funds || [];
    const matches = q ? list.filter((f) => (f.organization || '').toLowerCase().includes(q)) : list;
    return matches.slice(0, 25);
  }, [data?.funds, fundSearch]);

  const tickerSymbol = (data?.identity?.ticker || ticker || '').toString().toUpperCase();
  const faqItems = useMemo(() => buildFaqItems(tickerSymbol), [tickerSymbol]);
  const codeSnippets = useMemo(() => buildCodeSnippets(tickerSymbol, apiBaseUrl), [tickerSymbol, apiBaseUrl]);
  const researchExamples = useMemo(() => buildResearchExamples(tickerSymbol, data?.identity?.sector), [
    tickerSymbol,
    data?.identity?.sector,
  ]);
  const mastheadDate = useMemo(() => formatMastheadDate(), []);

  const handleCopy = () => {
    if (typeof navigator !== 'undefined' && navigator.clipboard) {
      navigator.clipboard
        .writeText(codeSnippets[activeTab])
        .then(() => {
          setCopied(true);
          setTimeout(() => setCopied(false), 1500);
        })
        .catch(() => {});
    }
  };

  if (!data || data.notFound || !data.identity) {
    return (
      <StyledTickerReport className={className} style={{ '--accent': accent }}>
        <div className="not-found">
          <AlertCircle size={22} aria-hidden="true" />
          <p>
            We couldn&apos;t find market data for <strong>{String(ticker || '').toUpperCase()}</strong>. Double-check
            the ticker symbol, or{' '}
            <a href={docsUrl} target="_blank" rel="noopener noreferrer">
              browse the API docs
            </a>
            .
          </p>
        </div>
      </StyledTickerReport>
    );
  }

  const {
    identity,
    logoUrl,
    profile,
    statistics,
    summary,
    recommendation,
    revenue,
    dcf,
    metrics,
    esg,
    ownership,
    transactions,
    funds,
    suppliers,
    customers,
    peers,
    sentiment,
    filings,
    news,
  } = data;

  const currency = identity.currency || 'USD';
  const changePositive = (identity.changePct || 0) >= 0;

  const statCards = [
    { icon: DollarSign, label: 'Market Cap', value: compactCurrency(summary?.marketCap) },
    {
      icon: BarChart3,
      label: 'P/E (TTM)',
      value: summary?.trailingPE ? `${summary.trailingPE.toFixed(1)}x` : '-',
    },
    {
      icon: Activity,
      label: 'EPS (TTM)',
      value: statistics?.trailingEps ? formatPrice(statistics.trailingEps, currency) : '-',
    },
    {
      icon: Percent,
      label: 'Dividend Yield',
      value: summary?.dividendYield !== undefined ? formatPercent(summary.dividendYield) : '-',
    },
    { icon: Gauge, label: 'Beta', value: statistics?.beta ? statistics.beta.toFixed(2) : '-' },
    {
      icon: TrendingUp,
      label: '52W High',
      value: summary?.fiftyTwoWeekHigh ? formatPrice(summary.fiftyTwoWeekHigh, currency) : '-',
    },
    {
      icon: TrendingDown,
      label: '52W Low',
      value: summary?.fiftyTwoWeekLow ? formatPrice(summary.fiftyTwoWeekLow, currency) : '-',
    },
    {
      icon: BarChart3,
      label: 'Avg Volume',
      value: summary?.averageVolume ? compactNumber(summary.averageVolume) : '-',
    },
  ];

  const ledgerItems = [
    { key: 'prices', label: 'Historical prices (OHLCV)', available: !!data.prices?.length },
    { key: 'fundamentals', label: 'Financial statements & ratios', available: !!revenue?.length },
    { key: 'valuation', label: 'DCF valuation & analyst consensus', available: !!(dcf || recommendation?.length) },
    { key: 'ownership', label: 'Insider & institutional ownership', available: !!ownership },
    { key: 'funds', label: 'ETF & fund exposure', available: !!funds?.length },
    { key: 'esg', label: 'ESG scores', available: !!esg?.length },
    {
      key: 'supplychain',
      label: 'Supply chain & peers',
      available: !!(suppliers?.length || customers?.length || peers?.length),
    },
    { key: 'filings', label: 'SEC filings', available: !!filings?.length },
    { key: 'sentiment', label: 'News, social & analyst sentiment', available: !!sentiment },
    { key: 'news', label: 'Company news', available: !!news?.length },
  ];

  const orgSchema = {
    '@context': 'https://schema.org',
    '@type': 'Corporation',
    name: identity.name || tickerSymbol,
    tickerSymbol,
    ...(identity.exchange ? { exchange: identity.exchange } : {}),
    ...(profile?.website ? { url: profile.website } : {}),
    ...(profile?.fullTimeEmployees ? { numberOfEmployees: profile.fullTimeEmployees } : {}),
    ...(profile?.longBusinessSummary ? { description: truncate(profile.longBusinessSummary, 500) } : {}),
  };

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqItems.map((f) => ({
      '@type': 'Question',
      name: f.q,
      acceptedAnswer: { '@type': 'Answer', text: f.a },
    })),
  };

  const recommendationClass = dcf?.recommendation === 'Sell' ? 'sell' : 'buy';
  const dcfMax = dcf ? Math.max(identity.lastClose || 0, dcf.fairPrice || 0) * 1.15 : 0;
  const overvalued = dcf ? (identity.lastClose || 0) > (dcf.fairPrice || 0) : false;
  const week52Max = summary?.fiftyTwoWeekHigh ? summary.fiftyTwoWeekHigh * 1.05 : 0;

  const trustedList = trustedByLogos && trustedByLogos.length > 0 ? trustedByLogos : DEFAULT_TRUSTED_BY;
  const relatedCompanies = (peers || []).filter((p) => p.ticker).slice(0, 6);

  const companyName = identity.name || tickerSymbol;
  const metaLastPrice = formatPrice(identity.lastClose, currency);
  const metaDayChange = formatPercent(identity.changePct, { decimals: 2, alreadyScaled: true });
  const metaMarketCap = compactCurrency(summary?.marketCap);
  const shortName = shortCompanyName(companyName);
  const descName = shortName.length <= 25 ? `${shortName} (${tickerSymbol})` : tickerSymbol;
  const metaDescription = [
    `${descName} stock price of ${metaLastPrice}`,
    metaDayChange !== '-' ? `(${metaDayChange} today)` : '',
    `market cap ${metaMarketCap},`,
    `plus financials, valuation and alternative data via the AxionQuant API.`,
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <>
      <SEOMetadata
        title={tickerSeoTitle(companyName, tickerSymbol)}
        description={metaDescription}
        canonicalUrl={`https://axionquant.com/stock/${tickerSymbol}`}
      />
      <StyledTickerReport className={className} style={{ '--accent': accent }}>
      <article className="report" data-ticker={tickerSymbol}>
        <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(orgSchema) }} />
        <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }} />

        {/* ---------------------------------------------------------- masthead */}
        <div className="masthead" role="presentation">
          <span className="masthead-brand">
            <Database size={13} aria-hidden="true" /> Axion Financial Data - Ticker Report
          </span>
          <span className="masthead-meta">
            {identity.exchange || identity.symbol || 'NASDAQ'}: {tickerSymbol} · {mastheadDate}
          </span>
        </div>

        {/* ---------------------------------------------------------- hero */}
        <header className="hero">
          <div className="hero-top">
            <div className="hero-identity">
              <div className="hero-name-row">
                {inlineLogoSvg ? (
                  <span
                    role="img"
                    aria-label={`${identity.name || tickerSymbol} logo`}
                    className="hero-logo hero-logo-inline"
                    dangerouslySetInnerHTML={{ __html: inlineLogoSvg }}
                  />
                ) : logoUrl && !logoError ? (
                  <img
                    src={logoUrl}
                    alt={`${identity.name || tickerSymbol} logo`}
                    className="hero-logo"
                    onError={() => setLogoError(true)}
                  />
                ) : (
                  <TickerAvatar ticker={tickerSymbol} size={52} />
                )}
                <h1 id="ticker-heading">
                  {identity.name || tickerSymbol} <span className="ticker-symbol">({tickerSymbol})</span>
                </h1>
              </div>
              <div className="hero-badge-row">
                <span className="exchange-badge">{identity.symbol || `${identity.exchange}:${tickerSymbol}`}</span>
                <span className="type-badge">
                  <Building2 size={12} aria-hidden="true" /> {identity.type || 'Equity'}
                </span>
              </div>
              <p className="hero-tagline">
                Live and historical stock data for {identity.name || tickerSymbol}, delivered through Axion&apos;s
                financial data API - pricing, fundamentals, SEC filings, ownership, ETF exposure and sentiment in
                one JSON response.
              </p>
              {(identity.sector || identity.industry || identity.country) && (
                <div className="pill-row">
                  {identity.sector && (
                    <span className="pill">
                      <Briefcase size={12} aria-hidden="true" /> {identity.sector}
                    </span>
                  )}
                  {identity.industry && (
                    <span className="pill">
                      <Building2 size={12} aria-hidden="true" /> {identity.industry}
                    </span>
                  )}
                  {identity.country && (
                    <span className="pill">
                      <Globe2 size={12} aria-hidden="true" /> {identity.country}
                    </span>
                  )}
                </div>
              )}
              {(profile?.website || profile?.city || profile?.fullTimeEmployees) && (
                <div className="detail-row">
                  {profile?.website && (
                    <a className="detail-item" href={profile.website} target="_blank" rel="noopener noreferrer">
                      <Globe2 size={13} aria-hidden="true" /> {profile.website.replace(/^https?:\/\//, '')}
                    </a>
                  )}
                  {(profile?.city || profile?.state || profile?.country) && (
                    <span className="detail-item">
                      <Building2 size={13} aria-hidden="true" />{' '}
                      {[profile.city, profile.state || profile.country].filter(Boolean).join(', ')}
                    </span>
                  )}
                  {profile?.fullTimeEmployees && (
                    <span className="detail-item">
                      <Users size={13} aria-hidden="true" /> {compactNumber(profile.fullTimeEmployees)} employees
                    </span>
                  )}
                </div>
              )}
              {profile?.longBusinessSummary && (
                <p className="hero-description">{truncate(profile.longBusinessSummary, 340)}</p>
              )}
            </div>

            <div className="hero-price">
              <div>
                <span className="price-label">Last Price</span>
                <div className="price-value">{formatPrice(identity.lastClose, currency)}</div>
                <div className={`price-change ${changePositive ? 'positive' : 'negative'}`}>
                  {changePositive ? <TrendingUp size={15} aria-hidden="true" /> : <TrendingDown size={15} aria-hidden="true" />}
                  {formatPercent(identity.changePct, { decimals: 2, alreadyScaled: true })} today
                </div>
              </div>
              <div className="hero-ctas">
                <a className="btn btn-primary" href={signupUrl} target="_blank" rel="noopener noreferrer">
                  Get free API key <ArrowRight size={15} aria-hidden="true" />
                </a>
                <a className="btn btn-ghost" href={docsUrl} target="_blank" rel="noopener noreferrer">
                  View docs
                </a>
              </div>
            </div>
          </div>

          <div className="hero-stats">
            {statCards.map((s) => (
              <StatCard key={s.label} icon={s.icon} label={s.label} value={s.value} />
            ))}
          </div>
        </header>

        {/* ------------------------------------------------------- price chart */}
        <section className="section" aria-labelledby="price-heading">
          <ChartCard
            eyebrowLabel="Historical Market Data API"
            endpoint={`GET /stocks/${tickerSymbol}/prices`}
            title={`${tickerSymbol} Price History`}
            description={`Weekly close prices for ${identity.name || tickerSymbol}, pulled directly from Axion's historical market data API.`}
          >
            <div className="range-toggle" role="group" aria-label="Chart date range">
              {RANGE_OPTIONS.map((r) => (
                <button key={r} type="button" className={r === range ? 'active' : ''} onClick={() => setRange(r)}>
                  {r}
                </button>
              ))}
            </div>
            {priceSeries.length > 1 ? (
              <ResponsiveContainer width="100%" height={340}>
                <AreaChart data={priceSeries} margin={{ top: 10, right: 12, left: 0, bottom: 0 }}>
                  <defs>
                    <linearGradient id="priceFill" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor={C_ACCENT} stopOpacity={0.35} />
                      <stop offset="100%" stopColor={C_ACCENT} stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="var(--hairline)" />
                  <XAxis dataKey="__label" tick={{ fontSize: 11 }} minTickGap={40} stroke="var(--subtext)" />
                  <YAxis
                    domain={['auto', 'auto']}
                    tick={{ fontSize: 11 }}
                    width={58}
                    tickFormatter={compactCurrency}
                    stroke="var(--subtext)"
                  />
                  <Tooltip content={<PriceTooltip currency={currency} />} />
                  <Area type="monotone" dataKey="close" stroke={C_ACCENT} strokeWidth={2} fill="url(#priceFill)" />
                </AreaChart>
              </ResponsiveContainer>
            ) : (
              <EmptyNote>No price history available for {tickerSymbol} yet.</EmptyNote>
            )}
          </ChartCard>
          <MoreLink href={signupUrl}>Get started with market data</MoreLink>
        </section>

        {/* --------------------------------------------------- fundamentals */}
        <section className="section" aria-labelledby="fundamentals-heading">
          <div className="section-head">
            <h2 id="fundamentals-heading">{tickerSymbol} Fundamentals - Financial Analytics API</h2>
            <p>
              Multi-year {identity.name || tickerSymbol} financial statement data, structured for a quantitative
              finance API workflow - no PDF parsing or scraping required.
            </p>
          </div>

          {metrics && (
            <div className="hero-stats margin-stats">
              <StatCard
                icon={Percent}
                label="Gross Margin"
                value={metrics.grossMargin !== undefined ? formatPercent(metrics.grossMargin) : '-'}
              />
              <StatCard
                icon={TrendingUp}
                label="Return on Equity"
                value={metrics.returnOnEquity !== undefined ? formatPercent(metrics.returnOnEquity) : '-'}
              />
              <StatCard
                icon={Gauge}
                label="Debt / Equity"
                value={metrics.debtToEquity !== undefined ? `${metrics.debtToEquity.toFixed(2)}x` : '-'}
              />
            </div>
          )}

          <div className="chart-grid two-col">
            <ChartCard
              eyebrowLabel="Financials API"
              endpoint={`GET /financials/${tickerSymbol}/revenue`}
              title="Revenue & Net Income"
              description="Annual revenue vs. net income, sourced from SEC filings."
            >
              {revenueNetIncome.length > 0 ? (
                <ResponsiveContainer width="100%" height={260}>
                  <BarChart data={revenueNetIncome}>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="var(--hairline)" />
                    <XAxis dataKey="period" tick={{ fontSize: 11 }} stroke="var(--subtext)" />
                    <YAxis tickFormatter={compactCurrency} tick={{ fontSize: 11 }} width={56} stroke="var(--subtext)" />
                    <Tooltip content={<ChartTooltip formatter={compactCurrency} />} />
                    <Legend wrapperStyle={{ fontSize: 12 }} />
                    <Bar dataKey="revenue" name="Revenue" fill={C_ACCENT} radius={[0, 0, 0, 0]} />
                    <Bar dataKey="netincome" name="Net Income" fill={C_POSITIVE} radius={[0, 0, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <EmptyNote>No revenue history available for {tickerSymbol}.</EmptyNote>
              )}
            </ChartCard>

            <ChartCard
              eyebrowLabel="Financials API"
              endpoint={`GET /financials/${tickerSymbol}/cashflow/free`}
              title="Free Cash Flow vs. CapEx"
              description="Cash generated after operating costs vs. capital reinvested into the business."
            >
              {fcfCapex.length > 0 ? (
                <ResponsiveContainer width="100%" height={260}>
                  <BarChart data={fcfCapex}>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="var(--hairline)" />
                    <XAxis dataKey="period" tick={{ fontSize: 11 }} stroke="var(--subtext)" />
                    <YAxis tickFormatter={compactCurrency} tick={{ fontSize: 11 }} width={56} stroke="var(--subtext)" />
                    <Tooltip content={<ChartTooltip formatter={compactCurrency} />} />
                    <Legend wrapperStyle={{ fontSize: 12 }} />
                    <Bar dataKey="fcf" name="Free Cash Flow" fill={C_ACCENT} radius={[0, 0, 0, 0]} />
                    <Bar dataKey="capex" name="CapEx" fill={C_MUTED} radius={[0, 0, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <EmptyNote>No cash flow history available for {tickerSymbol}.</EmptyNote>
              )}
            </ChartCard>

            <ChartCard
              eyebrowLabel="Financials API"
              endpoint={`GET /financials/${tickerSymbol}/eps`}
              title="EPS Trend (TTM)"
              description="Trailing twelve-month earnings per share, by report date."
            >
              {epsSeries.length > 0 ? (
                <ResponsiveContainer width="100%" height={260}>
                  <LineChart data={epsSeries}>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="var(--hairline)" />
                    <XAxis dataKey="__label" tick={{ fontSize: 11 }} stroke="var(--subtext)" />
                    <YAxis tick={{ fontSize: 11 }} width={48} tickFormatter={(v) => `$${v.toFixed(1)}`} stroke="var(--subtext)" />
                    <Tooltip content={<ChartTooltip formatter={(v) => `$${Number(v).toFixed(2)}`} />} />
                    <Line type="monotone" dataKey="__value" name="EPS (TTM)" stroke={C_ACCENT} strokeWidth={2} dot={false} />
                  </LineChart>
                </ResponsiveContainer>
              ) : (
                <EmptyNote>No EPS history available for {tickerSymbol}.</EmptyNote>
              )}
            </ChartCard>

            <ChartCard
              eyebrowLabel="Financials API"
              endpoint={`GET /financials/${tickerSymbol}/roe · /de`}
              title="Profitability & Leverage"
              description="Return on equity vs. debt-to-equity, by report date."
            >
              {roeDe.length > 0 ? (
                <ResponsiveContainer width="100%" height={260}>
                  <LineChart data={roeDe}>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="var(--hairline)" />
                    <XAxis dataKey="__label" tick={{ fontSize: 11 }} stroke="var(--subtext)" />
                    <YAxis
                      yAxisId="left"
                      tick={{ fontSize: 11 }}
                      width={48}
                      tickFormatter={(v) => formatPercent(v, { alreadyScaled: true, decimals: 0 })}
                      stroke="var(--subtext)"
                    />
                    <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 11 }} width={40} tickFormatter={(v) => `${v.toFixed(1)}x`} stroke="var(--subtext)" />
                    <Tooltip content={<ChartTooltip />} />
                    <Legend wrapperStyle={{ fontSize: 12 }} />
                    <Line yAxisId="left" type="monotone" dataKey="roe" name="ROE" stroke={C_ACCENT} strokeWidth={2} dot={false} />
                    <Line yAxisId="right" type="monotone" dataKey="debtToEquity" name="Debt/Equity" stroke={C_NEGATIVE} strokeWidth={2} dot={false} />
                  </LineChart>
                </ResponsiveContainer>
              ) : (
                <EmptyNote>No profitability history available for {tickerSymbol}.</EmptyNote>
              )}
            </ChartCard>
          </div>
          <MoreLink href={signupUrl}>Get started with financials</MoreLink>
        </section>

        {/* ------------------------------------------------------- valuation */}
        {(dcf || summary?.fiftyTwoWeekHigh || recommendationTrend.length > 0) && (
          <section className="section" aria-labelledby="valuation-heading">
            <div className="section-head">
              <h2 id="valuation-heading">{tickerSymbol} Valuation - Investment Research API</h2>
              <p>
                DCF fair value, analyst consensus and the 52-week trading range for {tickerSymbol}, built for
                investment research API and trading data API use cases.
              </p>
            </div>
            <div className="valuation-grid">
              {dcf && (
                <div className="valuation-card">
                  <SectionEyebrow endpoint={`GET /financials/dcf/${tickerSymbol}/value`} label="Quant Research API - DCF" />
                  <span className={`valuation-badge ${recommendationClass}`}>{dcf.recommendation}</span>
                  <div className="valuation-row">
                    <div>
                      <span className="label">Current Price</span>
                      <span className="value">{formatPrice(identity.lastClose, currency)}</span>
                    </div>
                    <div>
                      <span className="label">DCF Fair Value</span>
                      <span className="value">{formatPrice(dcf.fairPrice, currency)}</span>
                    </div>
                  </div>
                  <RangeBar
                    max={dcfMax}
                    segments={[{ from: 0, to: identity.lastClose, tone: overvalued ? 'negative' : 'positive' }]}
                    markers={[{ value: dcf.fairPrice, label: 'Fair value', tone: 'ink' }]}
                  />
                  <p className="margin-of-safety">
                    Margin of safety: <strong>{formatPercent(dcf.marginOfSafety, { alreadyScaled: true })}</strong>
                  </p>
                </div>
              )}

              {summary?.fiftyTwoWeekHigh && (
                <div className="valuation-card">
                  <SectionEyebrow endpoint={`GET /profiles/${tickerSymbol}/summary`} label="52-Week Range" />
                  <div className="valuation-row">
                    <div>
                      <span className="label">Current</span>
                      <span className="value">{formatPrice(identity.lastClose, currency)}</span>
                    </div>
                    <div>
                      <span className="label">Avg Volume</span>
                      <span className="value">{summary?.averageVolume ? compactNumber(summary.averageVolume) : '-'}</span>
                    </div>
                  </div>
                  <RangeBar
                    max={week52Max}
                    segments={[{ from: summary.fiftyTwoWeekLow, to: summary.fiftyTwoWeekHigh, tone: 'accent' }]}
                    markers={[{ value: identity.lastClose, label: 'Current price', tone: 'accent-strong' }]}
                  />
                  <p className="margin-of-safety">
                    52W Range: {formatPrice(summary.fiftyTwoWeekLow, currency)} - {formatPrice(summary.fiftyTwoWeekHigh, currency)}
                  </p>
                </div>
              )}
            </div>

            {recommendationTrend.length > 0 && (
              <ChartCard
                eyebrowLabel="Analyst Recommendations"
                endpoint={`GET /profiles/${tickerSymbol}/recommendation`}
                title="Analyst Consensus Trend"
                description="Strong buy / buy / hold / sell counts across recent trailing periods."
              >
                <ResponsiveContainer width="100%" height={220}>
                  <BarChart data={recommendationTrend}>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="var(--hairline)" />
                    <XAxis dataKey="period" tick={{ fontSize: 11 }} stroke="var(--subtext)" />
                    <YAxis tick={{ fontSize: 11 }} width={36} stroke="var(--subtext)" />
                    <Tooltip content={<ChartTooltip />} />
                    <Legend wrapperStyle={{ fontSize: 12 }} />
                    <Bar dataKey="strongBuy" name="Strong Buy" stackId="rec" fill={C_POSITIVE} />
                    <Bar dataKey="buy" name="Buy" stackId="rec" fill={C_ACCENT} />
                    <Bar dataKey="hold" name="Hold" stackId="rec" fill={C_MUTED} />
                    <Bar dataKey="sell" name="Sell" stackId="rec" fill={C_NEGATIVE} radius={[0, 0, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </ChartCard>
            )}
            <MoreLink href={signupUrl}>Learn more about valuation</MoreLink>
          </section>
        )}

        {/* -------------------------------------------------- ETF & fund exposure (searchable) */}
        {funds?.length > 0 && (
          <section className="section" aria-labelledby="funds-heading">
            <div className="section-head">
              <h2 id="funds-heading">ETF & Fund Exposure to {tickerSymbol}</h2>
              <p>
                Every mutual fund and ETF reporting a position in {tickerSymbol}, searchable by fund name - a
                direct look at institutional financial data API coverage for this ticker.
              </p>
            </div>
            <SectionEyebrow endpoint={`GET /insiders/${tickerSymbol}/funds`} label="ETF & Fund Ownership API" />
            <div className="table-toolbar">
              <Search size={15} aria-hidden="true" />
              <input
                type="text"
                placeholder={`Search funds holding ${tickerSymbol}…`}
                value={fundSearch}
                onChange={(e) => setFundSearch(e.target.value)}
                aria-label="Search funds"
              />
            </div>
            <div className="data-table-wrap">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Fund / ETF</th>
                    <th>Shares Held</th>
                    {filteredFunds[0]?.pctHeld !== undefined && <th>% Held</th>}
                    {filteredFunds[0]?.value !== undefined && <th>Value</th>}
                  </tr>
                </thead>
                <tbody>
                  {filteredFunds.map((f, i) => (
                    <tr key={`${f.organization}-${i}`}>
                      <td>{f.organization}</td>
                      <td>{f.shares !== undefined ? compactNumber(f.shares) : '-'}</td>
                      {f.pctHeld !== undefined && <td>{formatPercent(f.pctHeld)}</td>}
                      {f.value !== undefined && <td>{compactCurrency(f.value)}</td>}
                    </tr>
                  ))}
                  {filteredFunds.length === 0 && (
                    <tr>
                      <td colSpan={4}>No funds match &quot;{fundSearch}&quot;.</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
            <MoreLink href={signupUrl}>See more funds</MoreLink>
          </section>
        )}

        {/* ---------------------------------------------------------- related companies */}
        {(relatedCompanies.length > 0 || suppliers?.length > 0 || customers?.length > 0) && (
          <section className="section" aria-labelledby="related-heading">
            <div className="section-head">
              <h2 id="related-heading">Companies Linked to {tickerSymbol} - Trading Data API</h2>
              <p>
                Peers, suppliers and customers mapped from Axion&apos;s supply chain API, useful for building
                correlated, cross-company trading data API signals.
              </p>
            </div>

            {relatedCompanies.length > 0 && (
              <div className="related-grid">
                {relatedCompanies.map((p) => {
                  const cap = parseMoneyString(p.marketCap);
                  return (
                    <div key={p.ticker} className="related-card" >
                      <TickerAvatar ticker={p.ticker} size={40} />
                      <div>
                        <div className="related-name">{p.companyName || p.name}</div>
                        <div className="related-ticker">
                          {p.ticker}
                          {Number.isFinite(cap) ? ` · ${compactCurrency(cap * 1e6)}` : ''}
                        </div>
                      </div>
                      <ArrowUpRight size={16} aria-hidden="true" />
                    </div>
                  );
                })}
              </div>
            )}

            {(suppliers?.length > 0 || customers?.length > 0 || peers?.length > 0) && (
              <div className="supply-chain-grid">
                <SupplyChainColumn icon={Network} title="Key Suppliers" items={suppliers} />
                <SupplyChainColumn icon={Users} title="Major Customers" items={customers} />
                <SupplyChainColumn icon={Link2} title="Peers & Competitors" items={peers} />
              </div>
            )}
            <MoreLink href={signupUrl}>Learn more about supply chains</MoreLink>
          </section>
        )}

        {/* ---------------------------------------------------------------- ownership */}
        {ownership && (
          <section className="section" aria-labelledby="ownership-heading">
            <div className="section-head">
              <h2 id="ownership-heading">{tickerSymbol} Ownership - Institutional Financial Data API</h2>
              <p>Insider, institutional and public ownership breakdown, plus the latest insider transactions.</p>
            </div>
            <div className="ownership-card">
              <SectionEyebrow endpoint={`GET /insiders/${tickerSymbol}/ownership`} label="Ownership API" />
              <div className="ownership-flex">
                <ResponsiveContainer width="100%" height={200}>
                  <PieChart>
                    <Pie data={ownershipData} dataKey="value" nameKey="name" innerRadius={48} outerRadius={78} paddingAngle={2}>
                      {ownershipData.map((entry, index) => (
                        <Cell key={entry.name} fill={[C_ACCENT, C_POSITIVE, C_MUTED][index % 3]} />
                      ))}
                    </Pie>
                    <Tooltip content={<ChartTooltip formatter={(v) => `${v}%`} />} />
                    <Legend wrapperStyle={{ fontSize: 12 }} />
                  </PieChart>
                </ResponsiveContainer>
                {transactions?.length > 0 && (
                  <table className="mini-table">
                    <thead>
                      <tr>
                        <th>Insider</th>
                        <th>Activity</th>
                        <th>Shares</th>
                        <th>Value</th>
                      </tr>
                    </thead>
                    <tbody>
                      {transactions.slice(0, 5).map((t, i) => (
                        <tr key={i}>
                          <td>{t.filerName}</td>
                          <td>{t.transactionText}</td>
                          <td>{t.shares !== undefined ? compactNumber(t.shares) : '-'}</td>
                          <td>{t.value !== undefined ? compactCurrency(t.value) : '-'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            </div>
            <MoreLink href={signupUrl}>See more ownership data</MoreLink>
          </section>
        )}

        {/* -------------------------------------------------------------------- ESG */}
        {esgData.length > 0 && (
          <section className="section" aria-labelledby="esg-heading">
            <div className="section-head">
              <h2 id="esg-heading">{tickerSymbol} ESG Scores</h2>
              <p>Environmental, social, governance and controversy scores, pulled from the same financial data API.</p>
            </div>
            <ChartCard eyebrowLabel="ESG API" endpoint={`GET /esg/${tickerSymbol}`} title="ESG Score Breakdown">
              <ResponsiveContainer width="100%" height={260}>
                <BarChart data={esgData}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="var(--hairline)" />
                  <XAxis dataKey="category" tick={{ fontSize: 11 }} stroke="var(--subtext)" />
                  <YAxis tick={{ fontSize: 11 }} width={40} stroke="var(--subtext)" />
                  <Tooltip content={<ChartTooltip formatter={(v, n, p) => `${v} (${p?.payload?.grade || ''})`} />} />
                  <Bar dataKey="score" name="Score" fill={C_ACCENT} radius={[0, 0, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </ChartCard>
            <MoreLink href={signupUrl}>Learn more about ESG data</MoreLink>
          </section>
        )}

        {/* ------------------------------------------------------------------------ sentiment */}
        {sentiment && (
          <section className="section" aria-labelledby="sentiment-heading">
            <div className="section-head">
              <h2 id="sentiment-heading">{tickerSymbol} Market Sentiment - Financial Intelligence API</h2>
              <p>News, social and analyst sentiment for {tickerSymbol}, refreshed continuously.</p>
            </div>
            <div className="sentiment-grid">
              <SentimentCard icon={Newspaper} label="News Sentiment" data={sentiment.newsSentiment} endpoint={`GET /sentiment/${tickerSymbol}/news`} />
              <SentimentCard icon={Users} label="Social Sentiment" data={sentiment.socialSentiment} endpoint={`GET /sentiment/${tickerSymbol}/social`} />
              <SentimentCard
                icon={Star}
                label="Analyst Sentiment"
                data={sentiment.analystSentiment ? { label: sentiment.analystSentiment.sentiment, score: sentiment.analystSentiment.score } : null}
                endpoint={`GET /sentiment/${tickerSymbol}/analyst`}
              />
            </div>
            <MoreLink href={signupUrl}>Get started with sentiment data</MoreLink>
          </section>
        )}

        {/* -------------------------------------------------------------------- filings + news */}
        {(filings?.length > 0 || news?.length > 0) && (
          <section className="section">
            <div className="two-col-list">
              {filings?.length > 0 && (
                <div>
                  <SectionEyebrow endpoint={`GET /filings/${tickerSymbol}`} label="SEC Filings API" />
                  <h3>Recent SEC Filings</h3>
                  <ul className="link-list">
                    {filings.slice(0, 5).map((f) => (
                      <li key={f.accessionNumber}>
                        <span className="form-badge">{f.form}</span>
                        <a href={f.url} target="_blank" rel="noopener noreferrer">
                          {formatDateFull(f.filingDate)} <ExternalLink size={12} aria-hidden="true" />
                        </a>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              {news?.length > 0 && (
                <div>
                  <SectionEyebrow endpoint={`GET /news/${tickerSymbol}`} label="News API" />
                  <h3>Latest News</h3>
                  <ul className="link-list row">
                    {news.slice(0, 5).map((n, i) => (
                      <li key={i}>
                        <a href={n.link} target="_blank" rel="noopener noreferrer">
                          {n.title} <ExternalLink size={12} aria-hidden="true" />
                        </a>
                        <span className="news-date">{n.published}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
            <MoreLink href={signupUrl}>See more filings & news</MoreLink>
          </section>
        )}

        {/* --------------------------------------------------------------- see it in action */}
        <section className="section" aria-labelledby="action-heading">
          <div className="section-head">
            <h2 id="action-heading">See It In Action - API & Python Example</h2>
            <p>
              The same request that powers this page, in cURL, Python and JavaScript, plus an example chart rendered
              straight from the response.
            </p>
          </div>
          <div className="example-grid">
            <div>
              <div className="code-tabs">
                {Object.keys(codeSnippets).map((tab) => (
                  <button
                    key={tab}
                    type="button"
                    className={`code-tab ${activeTab === tab ? 'active' : ''}`}
                    onClick={() => setActiveTab(tab)}
                  >
                    {tab}
                  </button>
                ))}
              </div>
              <div className="code-panel">
                <button type="button" className="copy-btn" onClick={handleCopy}>
                  {copied ? <Check size={12} aria-hidden="true" /> : <Copy size={12} aria-hidden="true" />}
                  {copied ? 'Copied' : 'Copy'}
                </button>
                <pre>
                  <code>{codeSnippets[activeTab]}</code>
                </pre>
              </div>
            </div>
            <div className="example-chart-card">
              <SectionEyebrow endpoint={`GET /financials/${tickerSymbol}/revenue`} label="Example Chart" />
              <h3 className="chart-title">Rendered from the response above</h3>
              <p className="chart-desc">Revenue vs. net income - the exact data returned by the Python example.</p>
              {revenueNetIncome.length > 0 ? (
                <ResponsiveContainer width="100%" height={200}>
                  <BarChart data={revenueNetIncome}>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="var(--hairline)" />
                    <XAxis dataKey="period" tick={{ fontSize: 10 }} stroke="var(--subtext)" />
                    <YAxis tickFormatter={compactCurrency} tick={{ fontSize: 10 }} width={48} stroke="var(--subtext)" />
                    <Tooltip content={<ChartTooltip formatter={compactCurrency} />} />
                    <Bar dataKey="revenue" name="Revenue" fill={C_ACCENT} radius={[0, 0, 0, 0]} />
                    <Bar dataKey="netincome" name="Net Income" fill={C_POSITIVE} radius={[0, 0, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <EmptyNote>No data yet to chart for {tickerSymbol}.</EmptyNote>
              )}
            </div>
          </div>
          <MoreLink href={signupUrl}>Get started with the API</MoreLink>
        </section>

{/* ------------------------------------------------- data-coverage ledger */}
        <section className="ledger" aria-label={`Available datasets for ${tickerSymbol}`}>
          <div className="ledger-head">
            <Database size={16} aria-hidden="true" />
            <span>Every dataset below is one API call away for {tickerSymbol}</span>
          </div>
          <ul className="ledger-list">
            {ledgerItems.map((item) => (
              <li key={item.key} className={item.available ? 'available' : 'unavailable'}>
                {item.available ? <CheckCircle2 size={13} aria-hidden="true" /> : <Info size={13} aria-hidden="true" />}
                <span>{item.label}</span>
              </li>
            ))}
          </ul>
        </section>

        {/* ------------------------------------------------------------- why axion */}
        <section className="section" aria-labelledby="why-heading">
          <div className="section-head">
            <h2 id="why-heading">Why Developers Use Axion&apos;s Financial Data API</h2>
            <p>
              A single stock market API and market data API for pricing, fundamentals and alternative data - built
              for quant research API and institutional financial data API workflows.
            </p>
          </div>
          <div className="why-grid">
            {WHY_ITEMS.map((w) => (
              <div className="why-card" key={w.title}>
                <div className="why-card-icon">
                  <w.icon size={17} aria-hidden="true" />
                </div>
                <h3>{w.title}</h3>
                <p>{w.text}</p>
              </div>
            ))}
          </div>
        </section>


        {/* -------------------------------------------------------------------- research examples */}
        <section className="section" aria-labelledby="research-heading">
          <div className="section-head">
            <h2 id="research-heading">Research You Can Build on {tickerSymbol}</h2>
            <p>Starting points for quant research API and investment research API projects using this ticker.</p>
          </div>
          <div className="research-grid">
            {researchExamples.map((r) => (
              <a key={r.title} className="research-card" href={blogUrl} target="_blank" rel="noopener noreferrer" style={{ textDecoration: 'none', color: 'inherit' }}>
                <r.icon size={20} aria-hidden="true" />
                <h3>{r.title}</h3>
                <p>{r.text}</p>
              </a>
            ))}
          </div>
          <MoreLink href={signupUrl}>See more research examples</MoreLink>
        </section>

        {/* -------------------------------------------------------------------- products */}
        <section className="section" aria-labelledby="products-heading">
          <div className="section-head section-head-row">
            <div>
              <h2 id="products-heading">Explore More From Axion</h2>
              <p>One account, every dataset - browse the rest of Axion&apos;s market data API and alternative data catalog.</p>
            </div>
            <a className="btn btn-text" href={pricingUrl} target="_blank" rel="noopener noreferrer">
              See pricing <ArrowRight size={14} aria-hidden="true" />
            </a>
          </div>
          <div className="products-grid">
            {PRODUCTS.map((p) => (
              <a key={p.label} className="product-card" href={p.href} target="_blank" rel="noopener noreferrer">
                <div>
                  <div className="product-card-label">{p.label}</div>
                  <div className="product-card-desc">{p.desc}</div>
                </div>
                <ArrowUpRight size={15} aria-hidden="true" />
              </a>
            ))}
          </div>
        </section>

        {/* -------------------------------------------------------------------- trusted by */}
        <section className="section" aria-labelledby="trusted-heading">
          <div className="section-head">
            <h2 id="trusted-heading">
              <Award size={18} aria-hidden="true" style={{ verticalAlign: '-3px', marginRight: 8 }} />
              Built for Teams Like These
            </h2>
            <p>From independent researchers to institutional desks - Axion powers financial applications at every scale.</p>
          </div>
          <div className="trusted-grid">
            {trustedList.map((t) =>
              t.logoUrl ? (
                <span className="trusted-badge" key={t.name}>
                  <img className="trusted-logo" src={t.logoUrl} alt={t.name} />
                </span>
              ) : (
                <span className="trusted-badge" key={t.name}>
                  {t.name}
                </span>
              )
            )}
          </div>
        </section>

        {/* ------------------------------------------------------------------------- SEO copy */}
        <section className="seo-copy">
          <h2>{tickerSymbol} Data, Built for Developers and Quant Teams</h2>
          <p>
            {identity.name || tickerSymbol} pricing, fundamentals and alternative data are available through Axion&apos;s
            financial data API - a single, consistent stock data API and market data API covering equities, ETFs,
            crypto, forex and futures. Instead of stitching together a stock market API for pricing, a separate SEC
            filings feed, and a spreadsheet for fundamentals, pull {tickerSymbol}&apos;s revenue, EPS, insider
            activity, ETF exposure, ESG scores and DCF valuation from one financial analytics API.
          </p>
          <p>
            Axion is built for institutional financial data API and quant research API workflows: point-in-time
            financial statements for backtesting, a historical market data API with daily and weekly granularity
            going back years, and an investment research API layer that surfaces analyst consensus, sentiment and
            ownership changes for {tickerSymbol} alongside every other ticker you track. Whether you&apos;re building
            a quantitative finance API pipeline, a trading data API for a systematic strategy, or a research
            dashboard, {tickerSymbol}&apos;s complete dataset is one authenticated request away.
          </p>
          <div className="faq">
            <h3>Frequently asked questions</h3>
            {faqItems.map((f) => (
              <details key={f.q}>
                <summary>{f.q}</summary>
                <p>{f.a}</p>
              </details>
            ))}
          </div>
        </section>

        {/* --------------------------------------------------------------------- disclaimer */}
        <span className="disclaimer">
          <AlertCircle size={13} aria-hidden="true" />
          <span>
            Data delayed and provided for informational purposes only via the Axion financial data API. Not
            investment advice. Prices and fundamentals sourced from {tickerSymbol}&apos;s public filings and market
            data feeds.
          </span>
        </span>
      </article>
      </StyledTickerReport>

      <GetStarted />
      <Footer />
    </>
  );
};

export default AxionTickerReport;
